import { Divider, Stack } from '@vapor/material'
import { Tooltip, Typography } from '@mui/material'
import { Theme } from '@mui/system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductionAssignedFragment } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { BillF, DepositF } from '../types/reservation-detail.types'
import { reduceAssignedProductions } from '../utils/bills'
import { HiCIcon } from 'src/app/components/icons/icon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartShopping, faFileInvoice, faScaleBalanced, faWallet } from '@fortawesome/pro-light-svg-icons'

export type TotalsProps = {
  bills: BillF[]
  deposits: DepositF[]
}

export function Totals({ bills, deposits }: TotalsProps) {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()

  const pas = useMemo(() => reduceAssignedProductions(bills), [JSON.stringify(bills)])
  const pasAmount = useMemo(() => getProductionAssignedTotal(pas), [pas])
  const residualDepositAmount = useMemo(() => deposits.reduce((prev, current) => prev + (current.residualAmount ?? 0), 0), [JSON.stringify(deposits)])
  const closedPas = useMemo(() => reduceAssignedProductions(bills.filter((b) => !b.open)), [JSON.stringify(bills)])
  const closedPasAmount = useMemo(() => getProductionAssignedTotal(closedPas), [closedPas])
  const payAmount = useMemo(() => Math.max(0, pasAmount - residualDepositAmount - closedPasAmount), [pasAmount, residualDepositAmount, closedPasAmount])

  return (
    <Stack direction="row" gap={0.5}>
      <Stack alignItems="start" flexGrow={1}>
        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
          <HiCIcon>
            <FontAwesomeIcon icon={faCartShopping} />
          </HiCIcon>
          {t('pms.booking.reservation.totals.bills')}
        </Typography>
        <Typography variant="body">{formatCurrency(pasAmount, currency)}</Typography>
      </Stack>
      <Divider light orientation="vertical" flexItem />
      <Stack alignItems="start" flexGrow={1}>
        <Tooltip title={residualDepositAmount > pasAmount ? t('pms.booking.reservation.totals.deposit_residual_gt_bills') : null}>
          <span>
            <Typography
              variant="caption"
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={(theme: Theme) => ({
                color: residualDepositAmount > pasAmount ? theme.palette.error.main : undefined,
              })}
            >
              <HiCIcon>
                <FontAwesomeIcon icon={faWallet} />
              </HiCIcon>
              {t('pms.booking.reservation.totals.deposit_residual')}
            </Typography>
            <Typography
              variant="body"
              sx={(theme: Theme) => ({
                color: residualDepositAmount > pasAmount ? theme.palette.error.main : undefined,
              })}
            >
              {formatCurrency(residualDepositAmount, currency)}
            </Typography>
          </span>
        </Tooltip>
      </Stack>
      <Divider light orientation="vertical" flexItem />
      <Stack alignItems="start" flexGrow={1}>
        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
          <HiCIcon>
            <FontAwesomeIcon icon={faFileInvoice} />
          </HiCIcon>
          {t('pms.booking.reservation.totals.closed_bill')}
        </Typography>
        <Typography variant="body">{formatCurrency(closedPasAmount, currency)}</Typography>
      </Stack>
      <Divider light orientation="vertical" flexItem />
      <Stack alignItems="start" flexGrow={1}>
        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
          <HiCIcon>
            <FontAwesomeIcon icon={faScaleBalanced} />
          </HiCIcon>
          {t('pms.booking.reservation.totals.to_pay')}
        </Typography>
        <Typography variant="body700">{formatCurrency(payAmount, currency)}</Typography>
      </Stack>
    </Stack>
  )
}

function getProductionAssignedTotal(pas: ProductionAssignedFragment[]): number {
  const total = pas.reduce((prev, current) => {
    const quota = current.production.total * current.quota
    return prev + quota
  }, 0)

  return total
}
